import cx from 'classnames'

import Loader from 'common/components/loaders/Loader'
import RadialProgress from 'common/components/RadialProgress'
import useGetMilestoneMetricsQuery from 'features/homePage/api/queries/useGetMilestoneMetricsQuery'

import MilestoneContainer from './MilestoneContainer'
import MilestoneItem from './MilestoneItem'

interface Props {
  className?: string
}

const MilestoneTracker = ({ className = '' }: Props) => {
  const { isLoading, data } = useGetMilestoneMetricsQuery()

  if (isLoading) {
    return (
      <MilestoneContainer className={className}>
        <Loader data-testid="milestone-tracker-loader" />
      </MilestoneContainer>
    )
  }

  if (!data) {
    return (
      <MilestoneContainer
        className={cx('flex items-center justify-center', className)}
      >
        <div className="text-center">
          <h3 className="text-lg font-medium text-base-700">
            No milestone data available
          </h3>
          <p className="text-sm text-base-600 mt-1">
            Check back later for your quarterly progress
          </p>
        </div>
      </MilestoneContainer>
    )
  }

  const {
    totalPercentage,
    metrics: {
      broadcastCampaigns,
      liveTriggerCampaigns,
      totalExperimentsLaunched,
      uniqueVariantsSent,
      totalSends,
      channelsActivated,
    },
  } = data

  return (
    <MilestoneContainer className={cx('flex gap-4 items-center', className)}>
      <div className="flex items-center gap-3">
        <RadialProgress value={totalPercentage} />
        <span className="text-3xl text-base-700">
          Quarterly
          <br />
          Progress
        </span>
      </div>
      <div className="flex mx-auto max-w-180 w-full justify-evenly">
        <div className="flex flex-col mr-2.5">
          <MilestoneItem
            value={broadcastCampaigns.percentage}
            label="Broadcast campaigns"
            counter={{
              value: broadcastCampaigns.value,
              goal: broadcastCampaigns.goal,
            }}
          />
          <MilestoneItem
            value={liveTriggerCampaigns.percentage}
            label="Trigger campaigns"
            counter={{
              value: liveTriggerCampaigns.value,
              goal: liveTriggerCampaigns.goal,
            }}
          />
          <MilestoneItem
            value={totalExperimentsLaunched.percentage}
            label="Total experiments launched"
            counter={{
              value: totalExperimentsLaunched.value,
              goal: totalExperimentsLaunched.goal,
            }}
          />
        </div>
        <div className="flex flex-col">
          <MilestoneItem
            value={uniqueVariantsSent.percentage}
            label="Variants sent"
            counter={{
              value: uniqueVariantsSent.value,
              goal: uniqueVariantsSent.goal,
            }}
          />
          <MilestoneItem
            value={totalSends.percentage}
            label="Total sends"
            counter={{
              value: totalSends.value,
              goal: totalSends.goal,
            }}
          />
          <MilestoneItem
            value={channelsActivated.percentage}
            label="Channels activated"
            counter={{
              value: channelsActivated.value,
              goal: channelsActivated.goal,
            }}
          />
        </div>
      </div>
    </MilestoneContainer>
  )
}

export default MilestoneTracker
